import type { ExpertReportFeedbackItem } from '@/entities/expert-report';
import { richTextToReadableText } from '@/shared/lib';

/**
 * Значение правки как читаемый текст: у комментария снимается разметка, а границы абзацев
 * остаются переносами — иначе соседние абзацы слипаются в одно слово.
 */
export const formatFeedbackValue = (value: number | string | null): string => {
  if (value === null) return '';
  return typeof value === 'string' ? richTextToReadableText(value) : String(value);
};

/**
 * Содержимое столбца значения: правка либо показывается сравнением с предыдущим значением,
 * либо, если она этого значения не касалась, — действующим на тот момент значением без выделения.
 */
export type FeedbackCellValue = { kind: 'diff'; previous: string; next: string } | { kind: 'plain'; text: string };

/**
 * Столбец «Оценка»: изменённая оценка сравнивается с предыдущей, неизменённая показывается
 * действующей на момент правки. Пустая оценка после правки — возврат к рассчитанной системой,
 * поэтому сравнивается именно она.
 */
export const getRatingCell = (item: ExpertReportFeedbackItem): FeedbackCellValue =>
  item.ratingChanged
    ? {
        kind: 'diff',
        previous: formatFeedbackValue(item.ratingPreviousValue),
        next: formatFeedbackValue(item.ratingValue ?? item.ratingSystemValue),
      }
    : { kind: 'plain', text: formatFeedbackValue(item.ratingValue) };

/** Столбец «Комментарий» — по тем же правилам, что и столбец оценки */
export const getCommentCell = (item: ExpertReportFeedbackItem): FeedbackCellValue =>
  item.commentChanged
    ? {
        kind: 'diff',
        previous: formatFeedbackValue(item.commentPreviousValue),
        next: formatFeedbackValue(item.commentValue ?? item.commentSystemValue),
      }
    : { kind: 'plain', text: formatFeedbackValue(item.commentValue) };
